import { Trans } from '@lingui/react/macro';
import { Breadcrumbs, CardSection, SimpleCard, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { DateTimeDisplay } from '@/components/localization';
import Pagination from '@/components/Pagination';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAllServices, getProjectBookings } from '@/services/booking';
import { IState } from '@/state/IState';
import { useAsyncState } from '@/utils/hooks';
import { BreadcrumbsProjectLink } from '@/components/navigation';

type MeetingProps = { meetingId: number };

export const Meeting: React.FC<MeetingProps> = ({ meetingId: meetingsId }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject?.id);
    const [meetings] = useAsyncState(async () => (projectId ? await getAllServices(projectId) : null), [projectId], null);
    const meeting = useMemo(() => meetings?.find((x) => x.id == meetingsId), [meetings, meetingsId]);
    const [bookings] = useAsyncState(
        async () => (projectId ? await getProjectBookings(projectId, meetingsId) : null),
        [projectId],
        null
    );
    const [currentPage, setCurrentPage] = useState(1);
    const paginationState = useMemo(() => {
        return {
            currentPage: currentPage,
            pageSize: 24,
            totalEntries: bookings?.totalCount ?? 0,
            numberOfPages: bookings?.totalCount ? bookings?.totalCount / 25 : 1,
            setPage: (page: number) => {
                if (page > 0 && page <= bookings!.totalCount / 25) {
                    setCurrentPage(page);
                }
            },
            initialized: true,
            displayedEntries: {
                start: (currentPage - 1) * 25,
                end: currentPage * 25 - 1,
            },
        };
    }, [currentPage, bookings?.totalCount]);
    if (!meeting || !bookings) return <LoadingIndicator />;
    return (
        <>
            <SimplePageTitle title={meeting.name} />

            <Breadcrumbs>
                <BreadcrumbsProjectLink to="meetings/external">
                    <Trans>All meetings</Trans>
                </BreadcrumbsProjectLink>
                <Breadcrumbs.Item>{meeting.name}</Breadcrumbs.Item>
            </Breadcrumbs>
            <small>{meeting.description}</small>

            <Tabs.Container activeId="bookings" variant="underline" sticky>
                <Tabs.Tab id="bookings" label="Bookings" />
                <Tabs.Content>
                    <small>
                        <Trans>Count bookings: {bookings.totalCount}</Trans>
                    </small>
                    {bookings.meetings.map((booking) => {
                        return (
                            <SimpleCard>
                                <CardSection width="20%" grow caption={<Trans>Participant</Trans>}>
                                    {booking.participantName}
                                </CardSection>
                                <CardSection width="20%" grow caption={<Trans>Email</Trans>}>
                                    {booking.participantEmail}
                                </CardSection>
                                <CardSection width="20%" grow caption={<Trans>Time</Trans>}>
                                    <DateTimeDisplay value={booking.from} />
                                </CardSection>
                                <CardSection width="20%" grow caption={<Trans>Unit</Trans>}>
                                    {booking.residentialUnitName}
                                </CardSection>
                            </SimpleCard>
                        );
                    })}
                    <Pagination value={paginationState} />
                </Tabs.Content>
            </Tabs.Container>
        </>
    );
};
